/**
 * 曲と効果音の楽譜
 *
 * 【素材ではなく手続きで作る理由】
 * 音源ファイルを持たない。**著作権の確認が要らず、容量がほぼ増えず、
 * 電波の無い所でも鳴る**（PWA でオフラインに配っているので、
 * 数MBの音源を抱えると意味が薄れる）。
 *
 * 【ここも純粋な関数】
 * 返すのは「いつ・どの音を・どの楽器で」という並びだけで、
 * 音を出すのは `engine.ts` の仕事である。楽譜が数で書いてあるので、
 * 鳴らさずに検査できる。
 *
 * **史実の音楽ではない**（`scale.ts` の断りと同じ）。
 * 近世邦楽の音階と和楽器の鳴り方に寄せた合成音である。
 */

import { FastRng } from '@tenka/core';
import { beatSeconds, degreeToFreq, type ScaleName } from './scale';
import { VOICES, loop, mix, renderNote, type Voice, type VoiceName } from './voice';

/** 楽譜の1音 */
interface Note {
  /** 何拍目か */
  readonly beat: number;
  /** 音階の第何音か */
  readonly degree: number;
  readonly voice: VoiceName;
  /** 長さ（拍） */
  readonly beats: number;
  readonly gain: number;
}

export interface Scene {
  readonly scale: ScaleName;
  /** 根音の MIDI 番号 */
  readonly root: number;
  readonly bpm: number;
  /** 輪の長さ（拍） */
  readonly beats: number;
  readonly write: (rng: FastRng) => readonly Note[];
}

/** 種から「その並びのどれか」を選ぶ。`Math.random()` は使わない */
const pick = <T>(rng: FastRng, xs: readonly T[]): T => xs[rng.int(xs.length)]!;

/**
 * 場面ごとの曲。
 *
 * **速さが気分をいちばん強く決める。** 同じ音でも、遅ければ荘重に、
 * 速ければ急かして聞こえる。音色より先に速さを決めている。
 */
export const SCENES = {
  /** 表紙。名乗りの前の静けさ */
  title: {
    scale: 'ritsu',
    root: 57,
    bpm: 60,
    beats: 16,
    write: (rng) => {
      const out: Note[] = [];
      for (let b = 0; b < 16; b += 4) {
        out.push({ beat: b, degree: pick(rng, [0, 2, 4]), voice: 'shakuhachi', beats: 3.4, gain: 0.5 });
        out.push({ beat: b, degree: -7, voice: 'koto', beats: 2, gain: 0.35 });
      }
      out.push({ beat: 12, degree: 7, voice: 'suzu', beats: 1, gain: 0.2 });
      return out;
    },
  },
  /** 盤上。いちばん長く鳴るので、主張させない */
  board: {
    scale: 'ritsu',
    root: 57,
    bpm: 76,
    beats: 16,
    write: (rng) => {
      const out: Note[] = [];
      // 箏の分散和音。拍の裏は置かず、間を空ける
      for (let b = 0; b < 16; b++) {
        if (b % 2 === 1) continue;
        out.push({
          beat: b,
          degree: pick(rng, [0, 2, 4, 5, 7]),
          voice: 'koto',
          beats: 1.6,
          gain: 0.3,
        });
      }
      // 低い根音を4拍ごとに置いて、輪の切れ目を分かりやすくする
      for (let b = 0; b < 16; b += 4) {
        out.push({ beat: b, degree: -7, voice: 'koto', beats: 3, gain: 0.34 });
      }
      out.push({ beat: 8, degree: 4, voice: 'shakuhachi', beats: 5, gain: 0.28 });
      return out;
    },
  },
  /** 合戦。都節の半音と太鼓で張り詰めさせる */
  battle: {
    scale: 'miyakobushi',
    root: 50,
    bpm: 132,
    beats: 16,
    write: (rng) => {
      const out: Note[] = [];
      // 太鼓の刻み。表拍は必ず、裏拍はときどき
      for (let b = 0; b < 16; b++) {
        out.push({ beat: b, degree: 0, voice: 'taiko', beats: 0.4, gain: b % 4 === 0 ? 0.6 : 0.34 });
        if (rng.int(3) === 0) {
          out.push({ beat: b + 0.5, degree: 0, voice: 'taiko', beats: 0.3, gain: 0.2 });
        }
      }
      // 三味線の反復。同じ形を繰り返して、追い立てられる感じを出す
      const figure = [0, 1, 4, 1];
      for (let b = 0; b < 16; b++) {
        out.push({
          beat: b,
          degree: figure[b % figure.length]! + (b >= 8 ? 5 : 0),
          voice: 'shamisen',
          beats: 0.9,
          gain: 0.3,
        });
      }
      return out;
    },
  },
  /** ポーカー。間合いを聞かせたいので薄く */
  holdem: {
    scale: 'minyo',
    root: 55,
    bpm: 92,
    beats: 16,
    write: (rng) => {
      const out: Note[] = [];
      for (let b = 0; b < 16; b += 2) {
        out.push({ beat: b, degree: pick(rng, [0, 3, 4, 7]), voice: 'shamisen', beats: 1.4, gain: 0.26 });
      }
      for (let b = 0; b < 16; b += 8) {
        out.push({ beat: b, degree: -7, voice: 'koto', beats: 6, gain: 0.3 });
      }
      return out;
    },
  },
  /** 史実。止まって読む場面なので、拍を感じさせない */
  chronicle: {
    scale: 'miyakobushi',
    root: 50,
    bpm: 52,
    beats: 12,
    write: (rng) => {
      const out: Note[] = [];
      out.push({ beat: 0, degree: 0, voice: 'shakuhachi', beats: 5, gain: 0.42 });
      out.push({ beat: 6, degree: pick(rng, [2, 3, 4]), voice: 'shakuhachi', beats: 4.5, gain: 0.36 });
      out.push({ beat: 0, degree: -7, voice: 'koto', beats: 8, gain: 0.26 });
      out.push({ beat: 9, degree: 9, voice: 'suzu', beats: 1, gain: 0.16 });
      return out;
    },
  },
} as const satisfies Record<string, Scene>;

export type SceneName = keyof typeof SCENES;

/**
 * 場面の曲を1周ぶん作る。
 *
 * 種を渡すので、**同じ場面からは毎回同じ曲が出る**。
 * 開くたびに違う曲が鳴ると、覚えられず「知っている場所」にならない。
 */
export function renderScene(name: SceneName, sampleRate: number, seed = name): Float32Array {
  const scene = SCENES[name];
  const spb = beatSeconds(scene.bpm);
  const rng = new FastRng(`曲:${seed}`);
  const notes = scene.write(rng);
  const voiceRng = new FastRng(`雑音:${seed}`);
  const parts = notes.map((n) => ({
    at: n.beat * spb,
    samples: renderNote(
      VOICES[n.voice],
      degreeToFreq(scene.scale, scene.root, n.degree),
      n.beats * spb,
      sampleRate,
      voiceRng,
      n.gain,
    ),
  }));
  return loop(parts, scene.beats * spb, sampleRate);
}

// ---------------------------------------------------------------------------
// 効果音
// ---------------------------------------------------------------------------

interface Hit {
  readonly at: number;
  readonly voice: VoiceName;
  /** MIDI 番号で直に書く。効果音は音階に乗らなくてよい */
  readonly midi: number;
  readonly dur: number;
  readonly gain: number;
  /**
   * 楽器の設定を、この効果音のためだけに上書きする。
   *
   * **余韻がいちばん問題になる。** 箏の余韻は 0.5 秒あり、
   * 駒の跳ね（110ms ごと）に使うと5つ重なって濁った。
   * 楽器のほうを短くすると曲が痩せるので、効果音の側で切る。
   */
  readonly tweak?: Partial<Voice>;
}

/**
 * 効果音。**無音でも遊びが成立すること**（docs/04）を崩さないよう、
 * どれも「起きたことの合図」であって、情報はここにしか無い、とはしない。
 */
export const SFX = {
  /** さいころ。転がって止まる */
  dice: [
    { at: 0, voice: 'hyoushigi', midi: 74, dur: 0.05, gain: 0.5 },
    { at: 0.09, voice: 'hyoushigi', midi: 78, dur: 0.05, gain: 0.42 },
    { at: 0.17, voice: 'hyoushigi', midi: 71, dur: 0.05, gain: 0.36 },
    { at: 0.3, voice: 'hyoushigi', midi: 76, dur: 0.08, gain: 0.6 },
  ],
  /** 駒が1マス跳ねる。連打される（110ms ごと）ので、余韻まで詰める */
  hop: [
    {
      at: 0,
      voice: 'koto',
      midi: 84,
      dur: 0.03,
      gain: 0.22,
      tweak: { decay: 0.05, release: 0.03 },
    },
  ],
  /** 国を取った */
  seize: [
    { at: 0, voice: 'taiko', midi: 45, dur: 0.3, gain: 0.55 },
    { at: 0.12, voice: 'koto', midi: 69, dur: 0.5, gain: 0.4 },
    { at: 0.24, voice: 'koto', midi: 76, dur: 0.7, gain: 0.36 },
  ],
  /** 築城 */
  castle: [
    { at: 0, voice: 'hyoushigi', midi: 60, dur: 0.1, gain: 0.5 },
    { at: 0.14, voice: 'hyoushigi', midi: 60, dur: 0.1, gain: 0.45 },
    { at: 0.28, voice: 'taiko', midi: 43, dur: 0.4, gain: 0.5 },
  ],
  /** 札を切る */
  card: [{ at: 0, voice: 'hyoushigi', midi: 88, dur: 0.04, gain: 0.3, tweak: { release: 0.05 } }],
  /** 合戦の始まり。法螺貝 */
  battleStart: [
    { at: 0, voice: 'horagai', midi: 45, dur: 1.1, gain: 0.6 },
    { at: 0.5, voice: 'horagai', midi: 52, dur: 1.2, gain: 0.45 },
    { at: 1.2, voice: 'taiko', midi: 40, dur: 0.5, gain: 0.6 },
  ],
  /** 勝った */
  win: [
    { at: 0, voice: 'taiko', midi: 45, dur: 0.3, gain: 0.6 },
    { at: 0.18, voice: 'koto', midi: 69, dur: 0.6, gain: 0.45 },
    { at: 0.32, voice: 'koto', midi: 74, dur: 0.6, gain: 0.42 },
    { at: 0.46, voice: 'koto', midi: 81, dur: 1.2, gain: 0.45 },
    { at: 0.5, voice: 'suzu', midi: 93, dur: 1.4, gain: 0.2 },
  ],
  /** 負けた。下へ落とす */
  lose: [
    { at: 0, voice: 'shakuhachi', midi: 55, dur: 0.7, gain: 0.4 },
    { at: 0.35, voice: 'shakuhachi', midi: 50, dur: 1.1, gain: 0.34 },
  ],
  /** 史実が起きた。鈴で「読むもの」だと知らせる */
  chronicle: [
    { at: 0, voice: 'suzu', midi: 88, dur: 1.0, gain: 0.3 },
    { at: 0.22, voice: 'suzu', midi: 95, dur: 1.2, gain: 0.22 },
  ],
  /** 賭ける・積む */
  chip: [
    { at: 0, voice: 'hyoushigi', midi: 83, dur: 0.03, gain: 0.26 },
    { at: 0.05, voice: 'hyoushigi', midi: 86, dur: 0.03, gain: 0.2 },
  ],
  /** 退き陣 */
  fold: [{ at: 0, voice: 'shamisen', midi: 52, dur: 0.5, gain: 0.3 }],
} as const satisfies Record<string, readonly Hit[]>;

export type SfxName = keyof typeof SFX;

/** 効果音を1つ作る */
export function renderSfx(name: SfxName, sampleRate: number): Float32Array {
  const hits = SFX[name];
  const rng = new FastRng(`効果音:${name}`);
  const voiceOf = (h: Hit): Voice => (h.tweak ? { ...VOICES[h.voice], ...h.tweak } : VOICES[h.voice]);
  const parts = hits.map((h) => ({
    at: h.at,
    samples: renderNote(voiceOf(h), 440 * 2 ** ((h.midi - 69) / 12), h.dur, sampleRate, rng, h.gain),
  }));
  /*
   * 尺は「最後の音が消えきる所」まで。**輪にしないので回り込ませない** —
   * 効果音の尻尾が頭に回ると、鳴らすたびに頭が汚れる。
   */
  const end = hits.reduce((m, h) => Math.max(m, h.at + h.dur + voiceOf(h).release), 0);
  return mix(parts, end, sampleRate);
}
